import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Flame, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const { resetPassword, formatApiErrorDetail } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success("Password updated — please log in");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="mb-8 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand text-white"><Flame className="h-6 w-6" /></div>
          <span className="font-display text-xl font-extrabold">Meal Cost</span>
        </div>

        {!token ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
            <p className="font-semibold text-slate-700">Invalid reset link</p>
            <p className="mt-1 text-sm text-slate-500">This link is missing its token. Request a new one from the login page.</p>
            <Link to="/login" className="mt-4 inline-block text-sm font-semibold text-brand hover:underline">Back to log in</Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-brand"><ShieldCheck className="h-5 w-5" /><span className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Set a new password</span></div>
            <p className="mt-1 text-sm text-slate-500">Choose a new password for your account.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" required data-testid="reset-password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1.5" autoFocus />
              </div>
              <div>
                <Label htmlFor="cpw">Confirm password</Label>
                <Input id="cpw" type="password" required data-testid="reset-confirm-input" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="mt-1.5" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-submit-button"
                className="w-full rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-slate-500">
              <Link to="/login" className="font-semibold text-brand hover:underline">Back to log in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
